// ─── PART 1: CORE — constants, styles, helpers, shared primitives ─────────────
// Dependencies: react, recharts, lucide-react

import { useState, useEffect, useRef, useCallback, useId } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, AreaChart, Area, BarChart, Bar,
} from "recharts";
import { Settings, X, RefreshCw, AlertCircle, Zap, Wifi, WifiOff } from "lucide-react";

// ─── TAB MANIFEST ─────────────────────────────────────────────────────────────
export const TABS = [
  "SIGNAL BOARD",
  "MARKETS",
  "OPTIONS",
  "BONDS",
  "ECONOMY",
  "FILINGS",
  "ALT DATA",
  "DEEP SIGNALS",
  "WATCHLIST",
];

// ─── API BASE URLS ─────────────────────────────────────────────────────────────
export const FRED_BASE    = "https://api.stlouisfed.org/fred/series/observations";
export const TREASURY_BASE = "https://api.fiscaldata.treasury.gov/services/api/v1";
export const ALPACA_REST  = "https://data.alpaca.markets/v2";
export const TRADIER_BASE = "https://api.tradier.com/v1";
export const EIA_BASE     = "https://api.eia.gov/v2";

// ─── AI SYSTEM PROMPT ─────────────────────────────────────────────────────────
export const AI_SYSTEM_PROMPT = `You are a senior cross-asset analyst with 20 years experience across equities, fixed income, commodities, and derivatives. Deep expertise in options market structure, alternative data interpretation, macro analysis, and signal confluence identification.

Analyze data presented directly and precisely. No hedging language. No padding. Speak to a sophisticated counterpart.

When analyzing:
- State what the signal is and the mechanical reason it matters
- Quantify where possible (basis points, percentages, contract volumes)
- Flag anomalies explicitly — unusual volume, divergences, structural breaks
- Cite historical precedent by mechanism, not just outcome
- Rate signal strength: strong / moderate / weak with reasoning

What you do NOT do:
- Give specific buy/sell/hold recommendations on specific securities
- Give personalized financial advice
- Make price targets or timing predictions presented as certainties

End responses where relevant: "Not a recommendation — consult a licensed financial advisor for decisions."`;

// ─── COLOR TOKENS ─────────────────────────────────────────────────────────────
export const C = {
  bg:         "#0B0E14",   // Deep Obsidian
  panel:      "#1E2330",   // Midnight Slate
  panelAlt:   "#161b26",   // between bg and panel
  border:     "#1e2d40",
  borderHi:   "#2a3d55",

  amber:      "#00FF87",   // Quantum Emerald — primary accent
  amberDim:   "#007a42",
  amberBg:    "#001a0d",

  green:      "#22c55e",   // data up — never branding
  greenBg:    "#071a0f",

  red:        "#FF5252",   // Burnt Crimson — data down — never branding
  redBg:      "#1a0606",

  blue:       "#3b82f6",
  blueBg:     "#060e1a",

  teal:       "#00FF87",   // same as amber in this system
  tealBg:     "#001a0d",

  purple:     "#9D4EDD",   // Frequency Violet — AI/math features
  purpleBg:   "#0d0614",

  orange:     "#f97316",
  orangeBg:   "#120800",

  text:       "#E2E8F0",   // Liquid Platinum
  textMid:    "#8899aa",
  textDim:    "#4a5a6a",
};

// ─── TYPOGRAPHY ───────────────────────────────────────────────────────────────
export const font = "'JetBrains Mono','Fira Code','Consolas',monospace";

// ─── STYLE SYSTEM ─────────────────────────────────────────────────────────────
export const s = {
  app: {
    background: C.bg,
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    overflowX: "hidden",
    boxSizing: "border-box",
    fontFamily: font,
    color: C.text,
    display: "flex",
    flexDirection: "column",
    fontSize: 16,
  },

  header: {
    background: C.panel,
    borderBottom: `1px solid ${C.border}`,
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    height: 44,
    flexShrink: 0,
  },

  logo: {
    color: C.amber,
    fontWeight: 700,
    fontSize: 17,
    letterSpacing: "0.2em",
    marginRight: 16,
    whiteSpace: "nowrap",
  },

  tab: (active) => ({
    padding: "0 10px",
    height: 44,
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    letterSpacing: "0.12em",
    fontWeight: 700,
    cursor: "pointer",
    color: active ? C.amber : C.textDim,
    background: "none",
    border: "none",
    borderBottom: active ? `2px solid ${C.amber}` : "2px solid transparent",
    whiteSpace: "nowrap",
    transition: "color 0.1s",
  }),

  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },

  sidebar: {
    width: 160,
    minWidth: 0,
    background: C.panel,
    borderRight: `1px solid ${C.border}`,
    padding: 10,
    overflowY: "auto",
    flexShrink: 0,
    flexDirection: "column",
  },

  main: {
    flex: 1,
    // ⚠⚠ minWidth: 0 IS THE HALF THAT ACTUALLY STOPS THE GROWTH. A flex item's
    // default min-width is `auto` — its MIN-CONTENT width — so this container
    // could not shrink below its widest child and grew to match it. An 8-column
    // table (WatchlistTab:474, every header whiteSpace:nowrap) or a
    // `repeat(8, 1fr)` grid (MacroTab:190 — 1fr is minmax(AUTO, 1fr), so the
    // tracks have a min-content floor too) pushed this past the viewport on
    // every phone. s.sidebar above has carried minWidth: 0 all along; this was
    // the one flex child missing it.
    minWidth: 0,
    // ⚠⚠ overflowX WAS NEVER "visible" HERE — IT WAS SILENTLY "auto". Per CSS,
    // when one axis is set to anything but `visible` the other computes to
    // `auto`, so `overflowY: "auto"` alone made this a HORIZONTAL scroll
    // container. That is the page-level left-right swipe on Watchlist, Bonds,
    // Markets and Options: the swipe moved this box past s.panel's painted
    // background, so cells rendered on bare app background — "text floating with
    // no container" — while s.app's own overflowX:hidden kept the header and
    // sidebar still, which read as the content shifting underneath them.
    // ⚠ Stating it explicitly is the point. The implicit value was correct CSS
    // and completely invisible in the source.
    overflowX: "hidden",
    overflowY: "auto",
    padding: 12,
  },

  panel: {
    background: C.panel,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    marginBottom: 10,
  },

  panelH: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "7px 10px",
    borderBottom: `1px solid ${C.border}`,
  },

  panelT: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.18em",
    color: C.amber,
  },

  panelB: {
    padding: 10,
  },

  lbl: {
    fontSize: 11,
    color: C.textDim,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 2,
  },

  val: (color) => ({
    fontSize: 22,
    fontWeight: 700,
    color: color || C.text,
  }),

  badge: (up) => ({
    fontSize: 13,
    padding: "1px 5px",
    borderRadius: 2,
    background: up ? C.greenBg : C.redBg,
    color: up ? C.green : C.red,
    fontWeight: 700,
    border: `1px solid ${up ? C.green + "33" : C.red + "33"}`,
  }),

  btn: (variant = "default") => ({
    background:
      variant === "amber" ? C.amber :
      variant === "teal"  ? C.tealBg :
      variant === "green" ? C.greenBg :
      variant === "red"   ? C.redBg :
      "#111820",
    color:
      variant === "amber" ? "#000" :
      variant === "teal"  ? C.teal :
      variant === "green" ? C.green :
      variant === "red"   ? C.red :
      C.text,
    border: `1px solid ${
      variant === "amber" ? C.amber :
      variant === "teal"  ? C.teal + "55" :
      variant === "green" ? C.green + "55" :
      variant === "red"   ? C.red + "55" :
      C.border
    }`,
    borderRadius: 2,
    padding: "4px 10px",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.08em",
    cursor: "pointer",
    fontFamily: font,
    whiteSpace: "nowrap",
  }),

  inp: {
    background: "#070b0f",
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: "5px 8px",
    fontSize: 14,
    color: C.text,
    fontFamily: font,
    outline: "none",
    width: "100%",
  },

  tag: (color) => ({
    fontSize: 11,
    padding: "1px 5px",
    borderRadius: 2,
    background: "#0d1520",
    color: color || C.textDim,
    border: `1px solid ${color ? color + "33" : C.border}`,
    fontWeight: 600,
  }),

  aiBox: {
    background: "#040608",
    border: `1px solid ${C.amberDim}`,
    borderRadius: 3,
    padding: 10,
    marginTop: 8,
    fontSize: 14,
    lineHeight: 1.8,
    color: C.textMid,
    whiteSpace: "pre-wrap",
  },

  card: (accent) => ({
    background: C.panelAlt,
    border: `1px solid ${accent ? accent + "33" : C.border}`,
    borderRadius: 3,
    padding: 10,
    marginBottom: 8,
  }),

  statusDot: (status) => {
    const col =
      status === "live"    ? C.green :
      status === "delayed" ? C.amber :
      status === "mock"    ? C.red   :
      C.textDim;
    return {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: col,
      display: "inline-block",
      marginRight: 4,
      boxShadow: status === "live" ? `0 0 4px ${col}` : "none",
    };
  },
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
export const fmt  = (n, d = 2) => n == null || isNaN(n) ? "—" : Number(n).toFixed(d);
export const fmtB = (n) =>
  n == null ? "—" :
  n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` :
  n >= 1e9  ? `$${(n / 1e9).toFixed(1)}B`  :
              `$${(n / 1e6).toFixed(0)}M`;
export const fmtK = (n) =>
  n == null ? "—" :
  n >= 1e6  ? `${(n / 1e6).toFixed(1)}M` :
  n >= 1e3  ? `${(n / 1e3).toFixed(0)}K` :
              (n || 0).toString();
// Port of uo_classifier.fmt_money. Python rounds half-to-even; Math.round and
// toFixed round half up, so the ties are handled explicitly to keep output identical.
const _roundHalfEven = (x) => {
  const f = Math.floor(x);
  const d = x - f;
  return d < 0.5 ? f : d > 0.5 ? f + 1 : f % 2 === 0 ? f : f + 1;
};
const _fixed1 = (x) => {
  const m = x * 4;
  return Number.isInteger(m) && m % 2 !== 0 ? (_roundHalfEven(x * 10) / 10).toFixed(1) : x.toFixed(1);
};
const _fmtLarge = (v) =>
  Number(_fixed1(v / 1e6)) >= 1000 ? `$${_fixed1(v / 1e9)}B` : `$${_fixed1(v / 1e6)}M`;
export const fmtUsd = (value) => {
  const raw = Number(value || 0);
  if (!Number.isFinite(raw)) return "$0";
  const sign = raw < 0 ? "-" : "";
  const v = Math.abs(raw);
  if (v >= 1e6) return sign + _fmtLarge(v);
  if (v >= 1e3) {
    const k = _roundHalfEven(v / 1e3);
    return k >= 1000 ? sign + _fmtLarge(v) : `${sign}$${k.toLocaleString("en-US")}K`;
  }
  return `${sign}$${_roundHalfEven(v).toLocaleString("en-US")}`;
};
export const pct  = (n) => n == null || isNaN(n) ? "—" : `${n > 0 ? "+" : ""}${fmt(n)}%`;
export const bps  = (n) => n == null ? "—" : `${n > 0 ? "+" : ""}${Math.round(n * 100)}bps`;
export const clr  = (n) => !n ? C.text : n > 0 ? C.green : C.red;

// ─── NOUS ICON ────────────────────────────────────────────────────────────────
// Metatron's Cube as pure SVG. No image file, no dependency, sharp at any size.
//
// ⚠⚠ EVERY COORDINATE IS DERIVED FROM NOUS_R, NOT MEASURED OFF A REFERENCE
// IMAGE. The construction only holds because the numbers relate: the seed-of-life
// circles are HALF the hexagon's inradius and sit ONE radius from centre, which
// makes the six of them meet exactly at the hexagon's edge midpoints. That
// tangency is what generates the inner triangular grid. Hardcoding the points, or
// changing one radius without the other, breaks the grid off the geometry and the
// mark stops being Metatron's Cube — it just looks approximately like it.
// ⚠⚠ NousIcon geometry is duplicated in public/favicon.svg — if NOUS_R or the
// geometry constants change, regenerate both files together. The favicon cannot
// import from this module (it is a standalone asset served by the CDN before any
// JavaScript runs), so the two copies are kept in step by hand.
const NOUS_CYAN = "#00E5FF";
const NOUS_VB = 100;                                  // viewBox is 0 0 100 100
const NOUS_MID = NOUS_VB / 2;
// ⚠ 40, not 48: the glow below blurs at stdDeviation 2, which spreads ~6 user
// units past the stroke. A hexagon out at 48 would have its halo clipped by the
// viewBox on all six points.
const NOUS_R = 40;                                    // circumradius
const NOUS_INRADIUS = (NOUS_R * Math.sqrt(3)) / 2;    // edge-midpoint distance
const NOUS_RHO = NOUS_INRADIUS / 2;                   // seed-of-life circle radius

// −90° puts a vertex at top (pointy-top). SVG y grows downward; the figure is
// symmetric about the horizontal axis, so the flip is invisible.
const _nousAngles = [0, 1, 2, 3, 4, 5].map((k) => ((-90 + 60 * k) * Math.PI) / 180);
const _nousAt = (dist, a) => [
  +(NOUS_MID + dist * Math.cos(a)).toFixed(3),
  +(NOUS_MID + dist * Math.sin(a)).toFixed(3),
];
const NOUS_VERTS = _nousAngles.map((a) => _nousAt(NOUS_R, a));
const NOUS_SEEDS = _nousAngles.map((a) => _nousAt(NOUS_RHO, a));
const _nousPoly = (pts) => pts.map(([x, y]) => `${x},${y}`).join(" ");

// ⚠ `color` defaults to the cyan and MUST keep doing so: the share card and the
// public ledger already render this mark, and their look is not this change's
// business. Green is additive — a caller opts into it, nothing inherits it.
export function NousIcon({ size = 48, className, color = NOUS_CYAN }) {
  // ⚠ useId returns ":r0:" and those colons are not valid in an XML id. Stripped
  // rather than replaced with a counter so the id stays stable across renders and
  // survives SSR/hydration without two instances colliding on one filter.
  const glowId = `nous-glow-${useId().replace(/:/g, "")}`;

  // ⚠⚠ THE SPEC IS IN RENDERED PIXELS; THE ATTRIBUTE IS IN VIEWBOX UNITS. The
  // browser scales stroke-width by size/100, so a fixed attribute would make a
  // 24px mark hairline and a 200px one clumsy. Converting back is what holds the
  // pixel spec at every size.
  // ⚠ Half a pixel per DOUBLING, not linear: the spec fixes 1.5px at 48 and 2px
  // at 96. Linear scaling through those points would put 3px on a 96px mark and
  // fill in the inner grid solid.
  const px = Math.max(0.5, 1.5 + 0.5 * Math.log2(Math.max(1, size) / 48));
  const sw = +((px * NOUS_VB) / size).toFixed(3);

  return (
    <svg
      width={size} height={size} viewBox={`0 0 ${NOUS_VB} ${NOUS_VB}`}
      className={className} role="img" aria-label="Nous Topology"
      // ⚠ overflow visible — the filter region deliberately exceeds the viewBox
      // so the halo is not cut into a square around the mark.
      style={{ display: "block", overflow: "visible", flexShrink: 0 }}
    >
      <defs>
        <filter id={glowId} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="nousBlur" />
          {/* ⚠ The blur alone reads as a smear. Dropping its alpha is what turns
              it into a neon halo with the crisp stroke still legible on top. */}
          <feComponentTransfer in="nousBlur" result="nousGlow">
            <feFuncA type="linear" slope="0.55" />
          </feComponentTransfer>
          <feComposite in="SourceGraphic" in2="nousGlow" operator="over" />
        </filter>
      </defs>

      {/* ⚠ The glow needs no colour of its own. feGaussianBlur blurs the
          RENDERED source, so the halo is already whatever the stroke is — a
          separately-coloured glow layer would be a second thing to keep in
          sync for no gain. */}
      <g filter={`url(#${glowId})`} fill="none" stroke={color}
         strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {/* Seed of Life first — it is the faintest layer and the hard geometry
            has to read on top of it. */}
        <circle cx={NOUS_MID} cy={NOUS_MID} r={NOUS_RHO} opacity="0.5" />
        {NOUS_SEEDS.map(([cx, cy], i) => (
          <circle key={`seed-${i}`} cx={cx} cy={cy} r={NOUS_RHO} opacity="0.5" />
        ))}

        {/* Star of David — alternating vertices, two overlaid triangles. */}
        <polygon points={_nousPoly([NOUS_VERTS[0], NOUS_VERTS[2], NOUS_VERTS[4]])}
                 opacity="0.85" />
        <polygon points={_nousPoly([NOUS_VERTS[1], NOUS_VERTS[3], NOUS_VERTS[5]])}
                 opacity="0.85" />

        {/* Spokes, centre to each vertex. */}
        {NOUS_VERTS.map(([x, y], i) => (
          <line key={`spoke-${i}`} x1={NOUS_MID} y1={NOUS_MID} x2={x} y2={y}
                opacity="0.7" />
        ))}

        {/* Outer hexagon last, at full opacity — the strongest line in the mark. */}
        <polygon points={_nousPoly(NOUS_VERTS)} />
      </g>
    </svg>
  );
}

// ─── DATA STATUS BADGE ────────────────────────────────────────────────────────
// Unmissable live/delayed/representative/offline indicator on every panel.
// Red = representative data (not live). Never hidden or small.
export function DataStatus({ status, source, timestamp }) {
  const col =
    status === "live"    ? C.green :
    status === "delayed" ? C.amber :
    status === "mock"    ? C.red   :
    C.textDim;
  const label =
    status === "live"    ? "LIVE" :
    status === "delayed" ? "DELAYED" :
    status === "mock"    ? "REPRESENTATIVE" :
    "OFFLINE";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <span style={s.statusDot(status)} />
      <span style={{ fontSize: 11, color: col, fontWeight: 700 }}>{label}</span>
      {source    && <span style={{ fontSize: 11, color: C.textDim }}>· {source}</span>}
      {timestamp && <span style={{ fontSize: 11, color: C.textDim }}>· {timestamp}</span>}
    </div>
  );
}

// ─── ANALYST BUTTON ───────────────────────────────────────────────────────────
// Scoped AI analysis — fires against the data context of the calling panel.
// No free-form chat. Each button is bound to specific data it received.
// ── AI analyst output persistence (localStorage, per panel) ───────────────────
// A completed analysis is saved so it survives tab navigation / remounts until the
// user clears it or runs a new one. Best-effort: any localStorage failure is
// swallowed and the analyst still works, just without persistence.
function _analysisKey(panel) { return `qs_analysis_${panel}`; }
function loadAnalysis(panel) {
  try {
    const raw = localStorage.getItem(_analysisKey(panel));
    if (!raw) return null;
    const o = JSON.parse(raw);
    return o && typeof o.output === "string" ? o : null;
  } catch { return null; }
}
function saveAnalysis(panel, obj) {
  try { localStorage.setItem(_analysisKey(panel), JSON.stringify(obj)); } catch {}
}
function removeAnalysis(panel) {
  try { localStorage.removeItem(_analysisKey(panel)); } catch {}
}
// Best-effort ticker from the context string — AnalystButton has no ticker prop
// (the panels that own the symbol are out of this file's scope), so the ticker is
// read from the context where it names one. Returns null for panels whose context
// names no single ticker (Congress, tankers, market-wide scans, ...).
const _ANALYSIS_STOPWORDS = new Set(["THE", "AND", "FOR", "WITH", "THIS", "THAT", "YOUR", "EACH", "REFERENCE"]);
function _analysisTicker(context) {
  if (typeof context !== "string" || !context) return null;
  const m = context.slice(0, 240).match(/(?:analysis for|activity in|flow for|setup for|UNDERLYING:)\s+([A-Z][A-Z.]{0,5})\b/);
  return m && !_ANALYSIS_STOPWORDS.has(m[1]) ? m[1] : null;
}
function _etTimeLabel() {
  try {
    return new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false }) + " ET";
  } catch { return ""; }
}

// ⚠ `clientContext` is OPTIONAL and every existing caller omits it. When absent
// the spread below contributes nothing and the POST body is byte-identical to
// what it was — no other panel's behaviour changes.
export function AnalystButton({ context, panel = "signal_board", label = "ANALYZE",
                                globalSignals, clientContext }) {
  // Restore any persisted analysis for this panel on mount (lazy initializers run
  // once). A persisted result renders in the "done" state with a timestamp + CLEAR.
  const [state, setState] = useState(() => (loadAnalysis(panel) ? "done" : "idle"));
  const [text,  setText]  = useState(() => loadAnalysis(panel)?.output || "");
  const [meta,  setMeta]  = useState(() => {
    const p = loadAnalysis(panel);
    return p ? { timestamp: p.timestamp || "", ticker: p.ticker || null } : null;
  });
  const [remaining, setRemaining] = useState(null);
  const [errColor, setErrColor] = useState(null);   // per-error color for BYOK key failures
  const currentTicker = _analysisTicker(context);
  let tier = "";
  try { tier = localStorage.getItem("qs_tier") || ""; } catch {}
  const isNT = tier === "nous_topology";

  const analyze = async () => {
    setState("loading");
    setErrColor(null);
    // Owner-mode flag — opt-in per browser via localStorage. The backend still
    // gates it on an OWNER_EMAIL match, so this flag alone grants no bypass.
    let ownerMode = false;
    try { ownerMode = localStorage.getItem("qs_owner_mode") === "true"; } catch {}
    try {
      // Backend selects the model, builds the prompt, and routes the key.
      const res = await backendFetch(
        "https://api.anthropic.com/v1/messages",   // direct fallback (local dev)
        "/api/ai/analyze",
        {
          method: "POST",
          body: JSON.stringify({
            panel,
            context,
            ...(globalSignals ? { global_signals: globalSignals } : {}),
            // ⚠⚠ THE DIRECT PATH FOR RICH PANEL CONTEXT. Before this existed the
            // Convergence Board smuggled its synthesis context through
            // `global_signals` — a data bus meant to carry the cross-tab signal
            // state, not a panel's request payload. The backend accepted it from
            // either position; this is the position it should have come from.
            ...(clientContext ? { client_context: clientContext } : {}),
            ...(ownerMode ? { owner_mode: true } : {}),
          }),
        }
      );
      if (!res.ok) {
        let detail = "", errCode = "";
        try {
          const e = await res.json();
          const d = e && e.detail;
          if (d && typeof d === "object") { errCode = d.error || ""; detail = d.message || ""; }
          else { detail = d || ""; }
        } catch {}
        // Structured BYOK key errors — clear, actionable, color-coded.
        if (errCode === "invalid_key") {
          setErrColor(C.amber);
          setText("⚠ Invalid API Key\nYour key could not be verified.\nCheck console.anthropic.com");
        } else if (errCode === "credits_exhausted") {
          setErrColor(C.amber);
          setText("⚠ API Credits Exhausted\nAdd credits at console.anthropic.com/settings/billing");
        } else if (errCode === "rate_limited") {
          setErrColor(C.textDim);
          setText("⚠ Rate Limit Reached\nPlease wait a moment and try again.");
        } else {
          setText(
            res.status === 401 ? "Please sign in to use the AI analyst." :
            res.status === 403 ? "AI analyst is not available on your plan." :
            res.status === 429 ? (typeof detail === "string" && detail ? detail : "Daily AI limit reached. Try again tomorrow.") :
            res.status === 400 && /api key/i.test(String(detail)) ? "Add your Anthropic API key in Settings to enable the AI analyst." :
            (typeof detail === "string" && detail) || "Analysis unavailable."
          );
        }
        setState("error");
        return;
      }
      const data = await res.json();
      const output = data.analysis ?? "Analysis unavailable.";
      setText(output);
      if (typeof data.calls_remaining === "number") setRemaining(data.calls_remaining);
      // Persist so the output survives navigation until cleared / replaced.
      const m = { timestamp: _etTimeLabel(), ticker: _analysisTicker(context) };
      setMeta(m);
      saveAnalysis(panel, { output, ...m });
      setState("done");
    } catch {
      setText("Connection failed. Check your network.");
      setState("error");
    }
  };

  const reset = () => { setState("idle"); setText(""); setRemaining(null); setErrColor(null); };
  // Explicitly clear the persisted output (CLEAR ANALYSIS) → back to the button.
  const clearStored = () => { removeAnalysis(panel); setMeta(null); reset(); };
  // Stale-ticker path: drop the old analysis and immediately run a fresh one.
  const runNew = () => { removeAnalysis(panel); setMeta(null); analyze(); };

  // Fire on pointerdown so touch has no ~300ms click delay (identical to onClick
  // for mouse). The ref guard ignores rapid repeat taps so a run in flight can't
  // be double-submitted before React swaps in the disabled "SYNTHESIZING..." button.
  const busyRef = useRef(false);
  const handleAnalyze = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (busyRef.current || state !== "idle") return;
    busyRef.current = true;
    Promise.resolve(analyze()).finally(() => { busyRef.current = false; });
  };

  return (
    <div>
      {state === "idle" && (
        <button
          style={{ ...s.btn("teal"), minHeight: "44px", padding: "10px 16px" }}
          onPointerDown={handleAnalyze}
        >
          <Zap size={9} style={{ marginRight: 4, verticalAlign: "middle" }} />
          {label}
          {isNT && <span style={{ marginLeft: 6, color: C.amber }}>◈</span>}
        </button>
      )}
      {state === "loading" && (
        <button style={{ ...s.btn(), color: C.textDim }} disabled>
          SYNTHESIZING...
        </button>
      )}
      {(state === "done" || state === "error") && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 8, flexWrap: "wrap" }}>
            <span style={{ ...s.tag(C.teal), fontSize: 11 }}>⚡ ANALYST</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {state === "done" && meta?.timestamp && (
                <span style={{ fontSize: 10, color: C.textDim }}>Analysis from {meta.timestamp}</span>
              )}
              {state === "done" ? (
                <button style={{ ...s.btn(), padding: "2px 6px", fontSize: 10, color: C.textDim }} onClick={clearStored}>CLEAR ANALYSIS</button>
              ) : (
                <button style={{ ...s.btn(), padding: "2px 6px", fontSize: 11 }} onClick={reset}>✕ CLOSE</button>
              )}
            </div>
          </div>
          {state === "done" && meta?.ticker && currentTicker && meta.ticker !== currentTicker && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6, fontSize: 10, color: C.amber, lineHeight: 1.4 }}>
              <span>⚠ Analysis for {meta.ticker} — run new analysis for {currentTicker}</span>
              <button style={{ ...s.btn("amber"), padding: "1px 7px", fontSize: 9 }} onClick={runNew}>RUN NEW</button>
              <button style={{ ...s.btn(), padding: "1px 7px", fontSize: 9, color: C.textDim }} onClick={clearStored}>CLEAR</button>
            </div>
          )}
          <div style={{ ...s.aiBox, ...(state === "error" && errColor ? { color: errColor } : {}) }}>{text}</div>
          {state === "done" && remaining != null && (
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
              {remaining} AI {remaining === 1 ? "call" : "calls"} left today
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── LIVE TICKER (Alpaca WebSocket, 1s render throttle) ───────────────────────
// Connects to Alpaca IEX feed. Throttles renders to 1s to remain readable.
// Reconnects automatically on symbol change.
export function LiveTicker({ symbol, alpacaKey, alpacaSecret }) {
  const [quote,     setQuote]     = useState(null);
  const [connected, setConnected] = useState(false);
  const wsRef     = useRef(null);
  const lastRef   = useRef(0);
  const pendingRef = useRef(null);

  useEffect(() => {
    if (!alpacaKey || !alpacaSecret || !symbol) return;

    const ws = new WebSocket("wss://stream.data.alpaca.markets/v2/iex");
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: "auth", key: alpacaKey, secret: alpacaSecret }));
    };

    ws.onmessage = (e) => {
      try {
        JSON.parse(e.data).forEach((m) => {
          if (m.T === "success" && m.msg === "authenticated") {
            ws.send(JSON.stringify({ action: "subscribe", quotes: [symbol] }));
            setConnected(true);
          }
          if (m.T === "q" && m.S === symbol) {
            pendingRef.current = {
              bid:  m.bp,
              ask:  m.ap,
              time: new Date().toLocaleTimeString(),
            };
            if (Date.now() - lastRef.current > 1000) {
              setQuote({ ...pendingRef.current });
              lastRef.current = Date.now();
            }
          }
        });
      } catch {}
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => ws.close();

    return () => ws.close();
  }, [symbol, alpacaKey, alpacaSecret]);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={s.statusDot(connected ? "live" : "offline")} />
      <span style={{ fontSize: 11, color: connected ? C.green : C.textDim }}>
        {connected ? "LIVE" : "OFFLINE"}
      </span>
      {quote && (
        <>
          <span style={{ fontSize: 13 }}>
            B <span style={{ color: C.green }}>${fmt(quote.bid)}</span>
          </span>
          <span style={{ fontSize: 13 }}>
            A <span style={{ color: C.red }}>${fmt(quote.ask)}</span>
          </span>
          <span style={{ fontSize: 11, color: C.textDim }}>{quote.time}</span>
        </>
      )}
    </div>
  );
}

// ─── BACKEND FETCH UTILITY ────────────────────────────────────────────────────
// Drop-in replacement for fetch() that routes through backend when configured.
// ─── INTERNAL SESSION TOKEN ───────────────────────────────────────────────────
// ⚠⚠ ITS OWN KEY, DELIBERATELY SEPARATE FROM qs_auth_token. The two used to
// share one slot and whichever wrote last won: signing in to a normal account
// overwrote the internal token (AuthContext.applySession writes qs_auth_token on
// EVERY load while a Supabase session exists), and the internal login stomped a
// real user's session on the way in. Separate keys remove the collision instead
// of guarding each write site one at a time — qs_auth_token now belongs entirely
// to the Supabase flow, and nothing here ever touches it.
//
// ⚠ These live in core.jsx because it is the BOTTOM of the dependency graph:
// AuthContext already imports backendFetch from here, so the decode cannot live
// there without making the cycle core → AuthContext → core.
export const INTERNAL_TOKEN_KEY = "qs_internal_token";

function _ls(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** The payload of the internal token, or null. Pure read, never throws.
 *  ⚠ base64URL, not base64: JWT uses -/_ and drops padding, so a bare atob()
 *  rejects some tokens and accepts others. */
export function internalTokenPayload() {
  try {
    const token = _ls(INTERNAL_TOKEN_KEY);
    if (!token) return null;
    const seg = String(token).split(".")[1];
    if (!seg) return null;
    const b64 = seg.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)));
    return payload.internal === true ? payload : null;
  } catch {
    return null;
  }
}

/** True only for an internal token that has not expired. Pure read. */
export function internalTokenIsLive() {
  const p = internalTokenPayload();
  return !!p && typeof p.exp === "number" && p.exp > Date.now() / 1000;
}

// Set window.QUANT_SIGNAL_BACKEND = "https://api.quant-signal.ai" to activate.
// When backend is set, routes through it (caching, rate limits, auth);
// otherwise falls back to the direct API call (browser mode).
// A 30s client-side ceiling on EVERY request. fetch() has no default timeout, so
// a backend that accepts a connection but never responds would leave the promise
// pending forever and the caller's loading state stuck. AbortController turns that
// into a normal rejection (AbortError) the caller's existing catch already handles.
const BACKEND_FETCH_TIMEOUT_MS = 30000;
// AI synthesis (Nous Topology / Bubble Tray) can run up to ~120s server-side, so
// the /api/ai/analyze path gets a 150s ceiling — otherwise the 30s abort fires
// mid-synthesis and surfaces as "Connection failed. Check your network."
const AI_ANALYZE_TIMEOUT_MS = 150000;

export async function backendFetch(directUrl, backendPath, options = {}) {
  const backend = window.QUANT_SIGNAL_BACKEND;
  const timeoutMs = String(backendPath || "").includes("/api/ai/analyze")
    ? AI_ANALYZE_TIMEOUT_MS
    : BACKEND_FETCH_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    if (backend) {
      // ⚠⚠ THE INTERNAL TOKEN WINS WHEN IT IS LIVE. Without this the browser
      // would hold a valid internal credential and keep sending the Supabase one,
      // so the backend would read the caller as an ordinary subscriber and the
      // internal lens would be invisible even though the user is signed in to it.
      const token = internalTokenIsLive()
        ? _ls("qs_internal_token")
        : _ls("qs_auth_token");
      return await fetch(`${backend}${backendPath}`, {
        ...options,
        signal: controller.signal,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {}),
        },
      });
    }
    // No backend configured → direct call (local dev; keys likely absent → offline).
    return await fetch(directUrl, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}
